import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { serverFailure } from 'data/actions'
import { LOGIN_PATH } from 'data/auth/utils'
import { callApi } from 'duck/operations'
import { all, call, put, takeEvery } from 'redux-saga/effects'
import { ServerError, UnauthorizedError } from 'services/api/errors'
import { ActionType, getType } from 'typesafe-actions'
import { goToRoute } from 'utils/history'

import {
  changePasswordBegin,
  changePasswordFailure,
  changePasswordSuccess,
  loginWithEmail,
  resetPasswordBegin,
  resetPasswordFailure,
  resetPasswordSuccess,
} from './actions'
import { apiChangePassword, apiEmailLogin, apiSendRecoveryEmail } from './api'

export function* accountPageSaga() {
  yield all([
    takeEvery(getType(loginWithEmail.request), postEmailAndPassword),
    takeEvery(getType(changePasswordBegin), changePasswordSaga),
    takeEvery(getType(resetPasswordBegin), resetPasswordSaga),
  ])
}

function* postEmailAndPassword(action: ActionType<typeof loginWithEmail.request>) {
  const { email, password } = action.payload
  try {
    // This API call is special, and shouldn't be done using callApi method,
    // because of its special treatment of Forbidden / Unauthorized errors.
    const { userId, token } = yield call(apiEmailLogin, email, password)
    yield put(loginWithEmail.success({ userId, token }))
  } catch (error) {
    if (error instanceof ServerError) {
      yield put(serverFailure())
    } else {
      yield put(loginWithEmail.failure(error))
    }
  }
}

function* changePasswordSaga(action: ActionType<typeof changePasswordBegin>) {
  const { token, email, password, redirectPath } = action.payload
  try {
    yield callApi(apiChangePassword, email, password, token)
    goToRoute(redirectPath || LOGIN_PATH)
    yield put(changePasswordSuccess())
    yield showSuccessfulPasswordChange()
  } catch (error) {
    if (error instanceof UnauthorizedError) {
      yield put(changePasswordFailure())
    } else if (error instanceof ServerError) {
      yield put(changePasswordFailure())
    } else {
      throw error
    }
  }
}

function* resetPasswordSaga(action: ActionType<typeof resetPasswordBegin>) {
  const { email } = action.payload
  try {
    yield callApi(apiSendRecoveryEmail, email)
    goToRoute(LOGIN_PATH)
    yield put(resetPasswordSuccess())
    yield showSuccessfulEmailRecovery(email)
  } catch (error) {
    if (error instanceof UnauthorizedError) {
      yield put(resetPasswordFailure())
    } else if (error instanceof ServerError) {
      yield put(resetPasswordFailure())
    } else {
      throw error
    }
  }
}

function* showSuccessfulEmailRecovery(email: string) {
  yield put(
    showFlashNotice({
      message: `Email successfully sent to "${email}". Follow the steps provided in order to recover your account`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* showSuccessfulPasswordChange() {
  yield put(
    showFlashNotice({
      message: 'Password was successfully changed. You can now log in with your new password',
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}
